import React, { useState, useEffect } from 'react';
import { Container, Table, Badge, Form, Row, Col, Button, ButtonGroup, Pagination } from 'react-bootstrap';
import { FaDownload, FaFileAlt, FaFileCsv, FaFileExcel, FaFilePdf } from 'react-icons/fa';
import moment from 'moment';
import api, { getMediaUrl } from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import './Downloads.css';

const categoryMap = {
  forms: 'forms',
  reports: 'reports',
  'act&rules': 'acts',
  policies: 'policies',
  budgets: 'budgets',
  public: 'public',
  proceeding: 'proceeding',
  standing_proceeding: 'standing_proceeding',
  board_proceeding: 'board_proceeding',
  ration: 'ration',
  bpl_list: 'bpl_list',
  other: 'other',
};

const categoryLabelMap = {
  forms: 'Forms',
  reports: 'Reports',
  acts: 'Acts & Rules',
  policies: 'Policies',
  budgets: 'Budgets',
  public: 'Public Disclosure',
  proceeding: 'Proceeding',
  standing_proceeding: 'Standing Proceeding',
  board_proceeding: 'Board Proceeding',
  ration: 'Ration Card Details',
  bpl_list: 'BPL List',
  other: 'Other',
};


const Downloads = () => {
  const { category } = useParams(); // from URL
  const navigate = useNavigate();

  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(10);

  // Sync URL param to filter on first load
  useEffect(() => {
    if (category && categoryMap[category]) {
      setCategoryFilter(categoryMap[category]);
    } else {
      setCategoryFilter('');
    }
    setCurrentPage(1);
  }, [category]);

  useEffect(() => {
    loadDownloads();
  }, [categoryFilter, currentPage]);

  const loadDownloads = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        ...(categoryFilter ? { category: categoryFilter } : {})
      };
      const response = await api.get('/downloads/', { params });

      if (response.data.results) {
        setDownloads(response.data.results);
        setTotalCount(response.data.count);
      } else {
        setDownloads(response.data);
        setTotalCount(response.data.length);
      }
    } catch (error) {
      console.error('Error loading downloads:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Title', 'Category', 'Description', 'Size', 'Published', 'Downloads'];
    const csvData = downloads.map(dl => [
      dl.title,
      dl.category,
      dl.description || '',
      dl.file_size || '',
      moment(dl.published_date).format('YYYY-MM-DD'),
      dl.download_count
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `downloads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportToExcel = () => {
    const headers = '<tr><th>Title</th><th>Category</th><th>Description</th><th>Size</th><th>Published</th><th>Downloads</th></tr>';
    const rows = downloads.map(dl => `
      <tr>
        <td>${dl.title}</td>
        <td>${dl.category}</td>
        <td>${dl.description || ''}</td>
        <td>${dl.file_size || ''}</td>
        <td>${moment(dl.published_date).format('YYYY-MM-DD')}</td>
        <td>${dl.download_count}</td>
      </tr>
    `).join('');

    const table = `<table>${headers}${rows}</table>`;
    const blob = new Blob([table], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `downloads_${new Date().toISOString().split('T')[0]}.xls`;
    link.click();
  };

  const exportToPDF = () => {
    window.print();
  };

  const handleDownload = async (id) => {
    try {
      await api.post(`/downloads/${id}/download/`);
    } catch (error) {
      console.error('Error tracking download:', error);
    }
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setCategoryFilter(value);
    const urlSegment = Object.keys(categoryMap).find((k) => categoryMap[k] === value) || '';
    if (urlSegment) {
      navigate(`/downloads/${urlSegment}`);
    } else {
      navigate('/downloads');
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  const currentLabel = categoryFilter ? categoryLabelMap[categoryFilter] || categoryFilter : '';
  const headingSuffix = currentLabel ? ` - ${currentLabel}` : '';
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    let items = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let number = start; number <= end; number++) {
      items.push(
        <Pagination.Item key={number} active={number === currentPage} onClick={() => setCurrentPage(number)}>
          {number}
        </Pagination.Item>
      );
    }
    return (
      <div className="d-flex justify-content-center mt-4">
        <Pagination className="shadow-sm">
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} />
          {start > 1 && <Pagination.Ellipsis disabled />}
          {items}
          {end < totalPages && <Pagination.Ellipsis disabled />}
          <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    );
  };

  return (
    <Container className="downloads-page pb-5">
      <div className="downloads-title-section">
        <div>
          <h1 className="mb-1">Downloads{headingSuffix}</h1>
          <p className="text-muted small mb-0">
            Access and download official forms, reports, and documents.
          </p>
        </div>

        <div className="d-flex flex-wrap gap-3 align-items-center">
          <Form.Group controlId="categoryFilter" className="d-flex align-items-center gap-2">
            <Form.Label className="small fw-bold text-muted mb-0 text-nowrap">Filter:</Form.Label>
            <Form.Select
              size="sm"
              value={categoryFilter}
              onChange={handleCategoryChange}
              style={{ minWidth: '150px' }}
            >
              <option value="">All Categories</option>
              {Object.entries(categoryLabelMap).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <ButtonGroup className="shadow-sm">
            <Button variant="outline-success" size="sm" onClick={exportToCSV}>
              <FaFileCsv /> CSV
            </Button>
            <Button variant="outline-primary" size="sm" onClick={exportToExcel}>
              <FaFileExcel /> Excel
            </Button>
            <Button variant="outline-danger" size="sm" onClick={exportToPDF}>
              <FaFilePdf /> PDF
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <div className="table-responsive shadow-sm rounded">
        <Table striped bordered hover className="downloads-table align-middle mb-0">
          <thead className="theme-table-header">
            <tr>
              <th style={{ width: '30%' }}>Title</th>
              <th style={{ width: '15%' }}>Category</th>
              <th>Description</th>
              <th style={{ width: '10%' }}>Size</th>
              <th style={{ width: '12%' }}>Date</th>
              <th style={{ width: '10%' }} className="text-center">Usage</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {downloads.length > 0 ? (
              downloads.map((download) => (
                <tr key={download.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <FaFileAlt className="me-2 text-primary" />
                      <span className="fw-bold">{download.title}</span>
                    </div>
                  </td>
                  <td>
                    <Badge bg="info" className="category-badge text-dark bg-opacity-25 px-2 py-1">
                      {categoryLabelMap[download.category] || download.category}
                    </Badge>
                  </td>
                  <td className="small text-muted">{download.description || '-'}</td>
                  <td><Badge bg="light" text="dark" className="border font-monospace">{download.file_size || 'N/A'}</Badge></td>
                  <td className="small">{moment(download.published_date).format('MMM DD, YYYY')}</td>
                  <td className="text-center">
                    <span className="small text-muted">{download.download_count} DLs</span>
                  </td>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      href={getMediaUrl(download.file)}
                      target="_blank"
                      onClick={() => handleDownload(download.id)}
                      className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                      style={{ width: '32px', height: '32px' }}
                    >
                      <FaDownload size={14} />
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-5 text-muted">
                  No documents found for this selection.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {renderPagination()}
    </Container>
  );
};

export default Downloads;
